import {
  Alert,
  Button,
  ButtonGroup,
  closeCurrentDialog,
  FormButton,
  FormControl,
  openDialog,
} from 'platform/components';
import {HStack, Show, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';

import {isNil, isNotNil, last} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  Payment,
  PaymentAllowedAction,
  usePostCheckoutPaymentFullCorrectionCancelMutation,
  usePostCheckoutPaymentPartialCorrectionCancelMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useBoolean, useNavigate} from 'shared/web';

import {PayDialog} from '../../AftersalesCheckoutPayment/components/PayDialog';
import {IssueRestrictionResponse} from '../../AftersalesCheckoutPayment/types/IssueRestrictionResponse';
import {
  ContextTarget,
  RestrictionActionContext,
  getAction,
  getMessage,
  shouldShowAlert,
} from '../constants/issueRestrictionAlerts';
import {CheckoutPaymentFormTypeV2} from '../types/CheckoutPaymentFormTypeV2';

interface CheckoutPaymentActionsV2Props extends RequiredTestIdProps {
  allowedActions: PaymentAllowedAction[];
  checkoutId: string;
  payment: Payment;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  onAddAnotherDepositClick?: () => Promise<unknown>;
  onRemoveDepositClick?: () => Promise<unknown>;
  isBalancePaymentIssued?: boolean;
  issueRestrictions?: IssueRestrictionResponse[];
  isIssueLoading?: boolean;
  isLazySaving?: boolean;
  isSubmitButtonDisabled?: boolean;
  control?: FormControl<CheckoutPaymentFormTypeV2>;
  contextTarget?: ContextTarget;
  contextId?: string;
  actionContext?: RestrictionActionContext;
  templateDropdown?: ReactNode;
}

export function CheckoutPaymentActionsV2(props: CheckoutPaymentActionsV2Props) {
  const navigate = useNavigate();

  const [postFullCorrectionCancel, {isLoading: isFullCorrectionCancelLoading}] =
    usePostCheckoutPaymentFullCorrectionCancelMutation();
  const [postPartialCorrectionCancel, {isLoading: isPartialCorrectionCancelLoading}] =
    usePostCheckoutPaymentPartialCorrectionCancelMutation();

  const [isAddLoading, startAddLoading, stopAddLoading] = useBoolean();
  const [isRemoveLoading, startRemoveLoading, stopRemoveLoading] = useBoolean();

  const control = props.control;

  const canPay = props.allowedActions.includes('PAY');
  const canRemoveDeposit =
    isNotNil(props.onRemoveDepositClick) &&
    !props.isBalancePaymentIssued &&
    props.payment.paymentState !== 'PAID' &&
    props.payment.paymentState !== 'PENDING';
  const canIssueInvoice =
    props.allowedActions.includes('ISSUE_INVOICE') ||
    props.allowedActions.includes('ISSUE_FULL_CORRECTION') ||
    props.allowedActions.includes('ISSUE_PARTIAL_CORRECTION');
  const canSelectTemplate = props.allowedActions.includes('SELECT_TEMPLATE');
  const canDiscardCorrection =
    props.allowedActions.includes('CANCEL_FULL_CORRECTION') ||
    props.allowedActions.includes('CANCEL_PARTIAL_CORRECTION');
  const isDiscardLoading = isFullCorrectionCancelLoading || isPartialCorrectionCancelLoading;

  const handleDiscardCorrection = () => {
    const cancelArg = {checkoutId: props.checkoutId, paymentId: props.payment.checkoutPaymentId};

    match(props.payment.discriminator)
      .with('BALANCE_CORRECTION_FULL', () =>
        postFullCorrectionCancel(cancelArg).unwrap().catch(handleApiError)
      )
      .with('BALANCE_CORRECTION_PARTIAL', () =>
        postPartialCorrectionCancel(cancelArg).unwrap().catch(handleApiError)
      )
      .otherwise(() => undefined);
  };

  const handlePay = () => {
    openDialog(
      <PayDialog
        checkoutId={props.checkoutId}
        payment={props.payment}
        onClose={closeCurrentDialog}
        data-testid={suffixTestId('payDialog', props)}
      />,
      {
        title: i18n.t('entity.invoice.labels.paymentOfInvoice', {
          number: last(props.payment.files ?? [])?.name,
        }),
      }
    );
  };

  const handleAddAnotherDeposit = () => {
    if (isNil(props.onAddAnotherDepositClick)) {
      return;
    }

    startAddLoading();
    props.onAddAnotherDepositClick().finally(stopAddLoading);
  };

  const handleRemoveDeposit = () => {
    if (isNil(props.onRemoveDepositClick)) {
      return;
    }

    startRemoveLoading();
    props.onRemoveDepositClick().finally(stopRemoveLoading);
  };

  return (
    <VStack spacing={4} width="100%">
      <Show when={isNotNilOrEmpty(props.issueRestrictions)}>
        <VStack spacing={2}>
          {props.issueRestrictions?.map((restriction) => {
            if (!shouldShowAlert(restriction.code, props.contextTarget)) {
              return null;
            }

            const message = getMessage(restriction.code);
            const action = getAction(
              restriction.code,
              props.contextId,
              props.contextTarget,
              props.actionContext
            );

            return (
              <Alert
                key={restriction.code}
                title={message}
                variant="error"
                type="banner"
                hyperlinks={
                  isNotNil(action) && isNotNil(action.href)
                    ? [
                        {
                          title: action.title ?? i18n.t('general.actions.view'),
                          onClick: () => {
                            if (isNotNil(action.href)) {
                              navigate(action.href);
                            }
                          },
                        },
                      ]
                    : undefined
                }
              />
            );
          })}
        </VStack>
      </Show>

      <HStack width="100%">
        {props.onAddAnotherDepositClick && (
          <Button
            variant="link"
            title={i18n.t('entity.checkout.addAnotherPayment')}
            leftIcon="content/add_circle"
            onClick={handleAddAnotherDeposit}
            isDisabled={isAddLoading || isRemoveLoading || props.isEditingDisabled}
            isLoading={isAddLoading}
            data-testid={suffixTestId('addAnotherDeposit', props)}
          />
        )}
        <Space fillAvailable />
        <HStack spacing={3} align="center">
          {canRemoveDeposit && (
            <Button
              variant="dangerLink"
              title={i18n.t('entity.checkout.removePayment')}
              onClick={handleRemoveDeposit}
              isDisabled={isRemoveLoading || props.isEditingDisabled || props.isLazySaving}
              isLoading={isRemoveLoading}
              data-testid={suffixTestId('removeDeposit', props)}
            />
          )}
          <Show when={canSelectTemplate}>{props.templateDropdown}</Show>
          <ButtonGroup>
            <Show when={canDiscardCorrection}>
              <Button
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                onClick={handleDiscardCorrection}
                isDisabled={props.isEditingDisabled || props.isCompletingDisabled}
                isLoading={isDiscardLoading}
                data-testid={suffixTestId('discardCorrection', props)}
              />
            </Show>
            <Show when={canPay}>
              <Button
                title={i18n.t('entity.order.actions.pay')}
                onClick={handlePay}
                isDisabled={
                  isRemoveLoading || props.isEditingDisabled || props.isCompletingDisabled
                }
                data-testid={suffixTestId('pay', props)}
              />
            </Show>
            <Show when={canIssueInvoice}>
              {isNotNil(control) && (
                <FormButton
                  control={control}
                  type="submit"
                  isLoading={props.isIssueLoading}
                  isDisabled={
                    props.isSubmitButtonDisabled ||
                    isRemoveLoading ||
                    isNotNilOrEmpty(props.issueRestrictions) ||
                    props.isCompletingDisabled ||
                    props.isIssueLoading
                  }
                  title={i18n.t('entity.checkout.actions.issuePayment')}
                  data-testid={suffixTestId('issuePayment', props)}
                />
              )}
            </Show>
          </ButtonGroup>
        </HStack>
      </HStack>
    </VStack>
  );
}
